import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

type AnnouncementBase = Database["public"]["Tables"]["announcements"]["Row"];
export type Announcement = AnnouncementBase & {
  audience_group_ids?: string[] | null;
  audience_user_ids?: string[] | null;
};
export type AnnouncementInsert = Database["public"]["Tables"]["announcements"]["Insert"];
export type ViewerAudience = "customer" | "staff";

export interface AudienceGroup {
  id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  member_count?: number;
}

export interface UserOption {
  id: string;
  full_name: string | null;
  email: string | null;
  role: string;
}

/* eslint-disable @typescript-eslint/no-explicit-any */
const sb = supabase as any;

export async function listAnnouncements(): Promise<Announcement[]> {
  const { data, error } = await sb
    .from("announcements")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data as Announcement[]) ?? [];
}

export async function listActiveAnnouncements(viewer: ViewerAudience): Promise<Announcement[]> {
  // Resolver applies role + group + ad-hoc user targeting (SECURITY DEFINER).
  void viewer;
  const { data, error } = await sb.rpc("get_active_announcements_for_viewer");
  if (error) throw new Error(error.message || "Failed to load announcements");
  return (data as Announcement[]) ?? [];
}

export async function upsertAnnouncement(input: Partial<Announcement> & { id?: string }) {
  if (input.id) {
    const { id, created_at: _c, updated_at: _u, ...rest } = input;
    void _c;
    void _u;
    const { data, error } = await sb
      .from("announcements")
      .update(rest)
      .eq("id", id)
      .select()
      .single();
    if (error) throw error;
    return data as Announcement;
  }
  const { data, error } = await sb
    .from("announcements")
    .insert({
      title: input.title ?? "",
      body: input.body ?? "",
      mode: input.mode ?? "banner",
      enabled: input.enabled ?? false,
      bg_color: input.bg_color ?? null,
      text_color: input.text_color ?? null,
      cta_label: input.cta_label ?? null,
      cta_url: input.cta_url ?? null,
      dismissible: input.dismissible ?? true,
      starts_at: input.starts_at ?? null,
      ends_at: input.ends_at ?? null,
      audience: input.audience ?? "customer",
      audience_group_ids: input.audience_group_ids ?? [],
      audience_user_ids: input.audience_user_ids ?? [],
    })
    .select()
    .single();
  if (error) throw error;
  return data as Announcement;
}

export async function deleteAnnouncement(id: string) {
  const { error } = await sb.from("announcements").delete().eq("id", id);
  if (error) throw error;
}

// ---------- Audience groups ----------

export async function listAudienceGroups(): Promise<AudienceGroup[]> {
  const { data, error } = await sb
    .from("audience_groups")
    .select("id, name, description, created_at, updated_at")
    .order("name", { ascending: true });
  if (error) throw new Error(error.message || "Failed");
  const groups = (data as AudienceGroup[]) ?? [];
  if (groups.length === 0) return [];
  const { data: rows } = await sb.from("audience_group_members").select("group_id");
  const counts = new Map<string, number>();
  for (const r of (rows as { group_id: string }[]) ?? []) {
    counts.set(r.group_id, (counts.get(r.group_id) ?? 0) + 1);
  }
  return groups.map((g) => ({ ...g, member_count: counts.get(g.id) ?? 0 }));
}

export async function createAudienceGroup(name: string, description?: string | null) {
  const { data, error } = await sb
    .from("audience_groups")
    .insert({ name, description: description ?? null })
    .select()
    .single();
  if (error) throw new Error(error.message || "Failed");
  return data as AudienceGroup;
}

export async function deleteAudienceGroup(id: string) {
  const { error } = await sb.from("audience_groups").delete().eq("id", id);
  if (error) throw new Error(error.message || "Failed");
}

export async function listGroupMembers(groupId: string): Promise<string[]> {
  const { data, error } = await sb
    .from("audience_group_members")
    .select("user_id")
    .eq("group_id", groupId);
  if (error) throw new Error(error.message || "Failed");
  return ((data as { user_id: string }[]) ?? []).map((r) => r.user_id);
}

export async function setGroupMembers(groupId: string, userIds: string[]) {
  const { error: delErr } = await sb
    .from("audience_group_members")
    .delete()
    .eq("group_id", groupId);
  if (delErr) throw new Error(delErr.message || "Failed");
  if (userIds.length === 0) return;
  const rows = userIds.map((uid) => ({ group_id: groupId, user_id: uid }));
  const { error: insErr } = await sb.from("audience_group_members").insert(rows);
  if (insErr) throw new Error(insErr.message || "Failed");
}

export async function listAllUsers(): Promise<UserOption[]> {
  const { data, error } = await supabase
    .from("profiles")
    .select("id, full_name, email, role")
    .order("full_name", { ascending: true });
  if (error) throw error;
  return (data ?? []) as UserOption[];
}
